import axios from 'axios'
import router from '../router/index.js'
import { Message } from 'element-ui'
import {
    getToken as readToken,
    getTokenType as readTokenType,
    getRefreshToken,
    saveAuth as persistAuth,
    clearAuth as dropAuth
} from './auth'

// 后端统一响应码：00000 表示成功
const SUCCESS_CODE = '00000'
// 令牌失效相关业务码，遇到需清空登录态并回登录页
const TOKEN_INVALID_CODES = ['A0230', 'A0231', 'A0232']
const BASE_URL = 'http://47.97.34.244:8989/api/v1'

const service = axios.create({
    baseURL: BASE_URL,
    timeout: 60000
})

// 令牌不再散落在各处，统一由 auth.js 管理存储位置
export function setAuth(auth, remember) {
    persistAuth(auth, remember)
}

export function getToken() {
    return readToken()
}

export function getAuthTokenType() {
    return readTokenType()
}

export function clearAuth() {
    dropAuth()
}

// 校验令牌时置位，让拦截器暂不自动跳转，由路由守卫统一决定去向
let validating = false

/**
 * 静默校验当前令牌是否仍然有效。
 * 命中 401 时内部会自动用 refreshToken 换一次新令牌并重放，
 * 只有两者都过期才会 reject。全程不弹提示、不自动跳转。
 */
export function validateToken() {
    validating = true
    return service
        .get('/users/me', { silent: true, timeout: 10000 })
        .then(res => {
            validating = false
            return res
        })
        .catch(err => {
            validating = false
            throw err
        })
}

/** 判断错误是否属于「令牌确实失效」，避免网络抖动把用户误踢下线 */
export function isTokenExpired(error) {
    if (!error || typeof error !== 'object') return false
    const status = error.response ? error.response.status : 0
    if (status === 401 || status === 403) return true
    if (TOKEN_INVALID_CODES.indexOf(String(error.code)) !== -1) return true
    return false
}

function toLogin() {
    dropAuth()
    // 校验令牌引发的失效交给路由守卫统一跳转，避免两处同时导航造成重复跳转
    if (validating) return
    if (router.currentRoute && router.currentRoute.path !== '/login') {
        router.replace({ path: '/login', query: { redirect: router.currentRoute.fullPath } })
    }
}

// 刷新令牌：用不带拦截器的裸实例，避免与业务请求相互递归
const rawRequest = axios.create({ baseURL: BASE_URL, timeout: 15000 })
let refreshing = null
function refreshAccessToken() {
    if (refreshing) return refreshing
    const refreshToken = getRefreshToken()
    if (!refreshToken) return Promise.reject(new Error('no refresh token'))

    refreshing = rawRequest
        .post('/auth/refresh-token', null, { params: { refreshToken } })
        .then(res => {
            const body = res.data || {}
            if (String(body.code) === SUCCESS_CODE && body.data && body.data.accessToken) {
                persistAuth(body.data)
                return body.data.accessToken
            }
            throw new Error(body.msg || '刷新令牌失败')
        })
        .catch(err => {
            toLogin()
            throw err
        })
        .finally(() => {
            refreshing = null
        })
    return refreshing
}

// 请求拦截器：每次现读 token，避免登录后请求头不更新
service.interceptors.request.use(config => {
    const token = readToken()
    if (token) {
        config.headers['Authorization'] = readTokenType() + ' ' + token
    }
    return config
}, error => {
    return Promise.reject(error)
})

// 响应拦截器：统一解包 { code, data, msg }，业务调用方直接拿到 data
service.interceptors.response.use(response => {
    const body = response.data

    // 非标准包装结构（如文件流、纯文本）原样返回
    if (!body || typeof body !== 'object' || !('code' in body)) return body

    if (String(body.code) === SUCCESS_CODE) return body.data === undefined ? null : body.data

    if (TOKEN_INVALID_CODES.indexOf(String(body.code)) !== -1) {
        Message.error(body.msg || '登录已失效，请重新登录')
        toLogin()
        return Promise.reject(body)
    }

    if (!response.config || !response.config.silent) {
        Message.error(body.msg || '请求失败')
    }
    return Promise.reject(body)
}, error => {
    const config = error.config || {}
    const status = error.response ? error.response.status : 0

    // 401：优先用 refresh token 换一次新令牌后重放，失败再跳登录
    if (status === 401 && getRefreshToken() && !config._retry) {
        config._retry = true
        return refreshAccessToken().then(token => {
            config.headers['Authorization'] = readTokenType() + ' ' + token
            return service(config)
        }).catch(() => Promise.reject(error))
    }

    if (status === 401) toLogin()

    if (!config.silent) {
        const msg = status === 401 ? '登录已失效，请重新登录'
            : status === 403 ? '没有权限执行该操作'
            : status === 404 ? '请求的资源不存在'
            : status >= 500 ? '服务器异常，请稍后重试'
            : error.message === 'Network Error' ? '网络连接失败，请检查网络'
            : '请求超时或服务器异常，请稍后重试'
        Message.error(msg)
    }
    return Promise.reject(error)
})

export default service
